import React, { useEffect, useRef } from "react";

/**
 * ChartsSection
 * ─────────────
 * Props:
 *   metrics          — merged metrics
 *   currency         — e.g. "AED"
 *   period           — period key
 *   thisPeriodLabel  — e.g. "May 2025"
 *   prevPeriodLabel  — e.g. "April 2025"
 */

const COLORS = {
    blue: "#3B82F6",
    blueA: "rgba(59,130,246,0.18)",
    green: "#10B981",
    red: "#EF4444",
    amber: "#F59E0B",
    gray: "#9CA3AF",
    grayA: "rgba(156,163,175,0.22)",
};

function loadChartJs(cb) {
    if (window.Chart) { cb(); return; }
    const s = document.createElement("script");
    s.src = "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
    s.onload = cb;
    document.head.appendChild(s);
}

// ── Bookings Bar Chart ────────────────────────────────────────────────────────
const BookingsBarChart = ({ bookingsByDay, thisPeriodLabel, prevPeriodLabel, isClinicService }) => {
    const ref = useRef(null);
    const chartRef = useRef(null);

    const currLabel = thisPeriodLabel || "This Period";
    const prevLabel = prevPeriodLabel || "Prev Period";

    useEffect(() => {
        if (!bookingsByDay?.length) return;
        loadChartJs(() => {
            if (chartRef.current) chartRef.current.destroy();

            // d.day is a day name ("Mon", "Tue" etc) from computeMetrics — use directly as label
            const labels = bookingsByDay.map((d) => d.day);

            chartRef.current = new window.Chart(ref.current, {
                type: "bar",
                data: {
                    labels,
                    datasets: [
                        {
                            label: currLabel,
                            data: bookingsByDay.map((d) => d.current ?? 0),
                            backgroundColor: COLORS.blue,
                            borderRadius: 4,
                            barPercentage: 0.6,
                        },
                        {
                            label: prevLabel,
                            data: bookingsByDay.map((d) => d.previous ?? 0),
                            backgroundColor: COLORS.grayA,
                            borderColor: COLORS.gray,
                            borderWidth: 1,
                            borderRadius: 4,
                            barPercentage: 0.6,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y}`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            ticks: { autoSkip: true, maxTicksLimit: 10, color: "#6B7280", font: { size: 11 } },
                            grid: { display: false },
                        },
                        y: {
                            beginAtZero: true,
                            ticks: { color: "#6B7280", font: { size: 11 } },
                            grid: { color: "rgba(0,0,0,0.05)" },
                        },
                    },
                },
            });
        });
        return () => { if (chartRef.current) chartRef.current.destroy(); };
    }, [bookingsByDay, currLabel, prevLabel]);

    return (
        <div className="chart-card">
            <div className="chart-card-header">
                <span className="chart-card-title">{isClinicService ? "Appointments by Day" : "Bookings by Day"}</span>
                <div className="chart-legend">
                    <span className="chart-legend-item">
                        <span className="chart-legend-dot" style={{ background: COLORS.blue }} />
                        {currLabel}
                    </span>
                    <span className="chart-legend-item">
                        <span className="chart-legend-dot" style={{ background: COLORS.gray, opacity: 0.5 }} />
                        {prevLabel}
                    </span>
                </div>
            </div>
            <div style={{ position: "relative", height: "220px" }}>
                <canvas ref={ref} />
            </div>
        </div>
    );
};

// ── Job Status Donut ──────────────────────────────────────────────────────────
const JobStatusDonut = ({ metrics }) => {
    const ref = useRef(null);
    const chartRef = useRef(null);

    const completed = metrics.totalJobsCompleted?.current ?? 0;
    const cancelled = metrics.cancelledCount?.current ?? 0;
    const noShows = metrics.noShowCount?.current ?? 0;
    const total = completed + cancelled + noShows;

    useEffect(() => {
        if (!total) return;
        loadChartJs(() => {
            if (chartRef.current) chartRef.current.destroy();
            chartRef.current = new window.Chart(ref.current, {
                type: "doughnut",
                data: {
                    labels: ["Completed", "Cancelled", "No-show"],
                    datasets: [{
                        data: [completed, cancelled, noShows],
                        backgroundColor: [COLORS.green, COLORS.red, COLORS.amber],
                        borderWidth: 2,
                        borderColor: "#fff",
                        hoverOffset: 6,
                    }],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "68%",
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const p = total ? ((ctx.parsed / total) * 100).toFixed(1) : 0;
                                    return ` ${ctx.label}: ${ctx.parsed} (${p}%)`;
                                },
                            },
                        },
                    },
                },
            });
        });
        return () => { if (chartRef.current) chartRef.current.destroy(); };
    }, [completed, cancelled, noShows, total]);

    const pctOf = (v) => total ? ((v / total) * 100).toFixed(1) : "0.0";

    return (
        <div className="chart-card">
            <div className="chart-card-header">
                <span className="chart-card-title">Job Status Breakdown</span>
            </div>
            {total === 0 ? (
                <div className="chart-empty">No job data</div>
            ) : (
                <>
                    <div style={{ position: "relative", height: "180px" }}>
                        <canvas ref={ref} />
                        <div className="donut-center-label">
                            <div className="donut-center-value">{total}</div>
                            <div className="donut-center-sub">Total</div>
                        </div>
                    </div>
                    <div className="donut-legend">
                        {[
                            { label: "Completed", value: completed, color: COLORS.green },
                            { label: "Cancelled", value: cancelled, color: COLORS.red },
                            { label: "No-show", value: noShows, color: COLORS.amber },
                        ].map(({ label, value, color }) => (
                            <div key={label} className="donut-legend-row">
                                <span className="donut-legend-dot" style={{ background: color }} />
                                <span className="donut-legend-label">{label}</span>
                                <span className="donut-legend-count">{value}</span>
                                <span className="donut-legend-pct">{pctOf(value)}%</span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// ── Revenue by Crew Bar Chart ─────────────────────────────────────────────────
const RevenueBarChart = ({ metrics, currency, thisPeriodLabel, prevPeriodLabel, crewSingular = "Crew", crewPlural = "Crew" }) => {
    const ref = useRef(null);
    const chartRef = useRef(null);

    // Prefer revenuePerCrewBreakdown; fall back to crewBreakdown
    // Use service_provider_name → crew_name → "Crew #id" in that priority
    const resolveName = (c) => {
        const n = c.service_provider_name || c.crew_name || "";
        return String(n).trim() !== "" && isNaN(Number(n))
            ? String(n).trim()
            : c.crew_id ? `${crewSingular} #${c.crew_id}` : "Unknown";
    };

    const breakdown = (metrics.revenuePerCrewBreakdown?.length
        ? metrics.revenuePerCrewBreakdown.map((c) => ({
            crew_name: resolveName(c),
            revenue: c.current ?? 0,
            prevRevenue: c.previous ?? 0,
        }))
        : (metrics.crewBreakdown || []).map((c) => ({
            crew_name: resolveName(c),
            revenue: c.revenue ?? 0,
            prevRevenue: c.prevRevenue ?? 0,
        }))
    );

    const currLabel = thisPeriodLabel || "This Period";
    const prevLabel = prevPeriodLabel || "Prev Period";

    useEffect(() => {
        if (!breakdown.length) return;
        loadChartJs(() => {
            if (chartRef.current) chartRef.current.destroy();
            chartRef.current = new window.Chart(ref.current, {
                type: "bar",
                data: {
                    labels: breakdown.map((c) => c.crew_name),
                    datasets: [
                        {
                            label: currLabel,
                            data: breakdown.map((c) => Math.round(c.revenue)),
                            backgroundColor: COLORS.green,
                            borderRadius: 4,
                            barPercentage: 0.55,
                        },
                        {
                            label: prevLabel,
                            data: breakdown.map((c) => Math.round(c.prevRevenue)),
                            backgroundColor: "rgba(16,185,129,0.18)",
                            borderColor: COLORS.green,
                            borderWidth: 1,
                            borderRadius: 4,
                            barPercentage: 0.55,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            callbacks: {
                                label: (ctx) =>
                                    ` ${ctx.dataset.label}: ${currency} ${ctx.parsed.y.toLocaleString()}`,
                            },
                        },
                    },
                    scales: {
                        x: {
                            ticks: { color: "#6B7280", font: { size: 11 } },
                            grid: { display: false },
                        },
                        y: {
                            beginAtZero: true,
                            ticks: {
                                color: "#6B7280",
                                font: { size: 11 },
                                callback: (v) => `${currency} ${v.toLocaleString()}`,
                            },
                            grid: { color: "rgba(0,0,0,0.05)" },
                        },
                    },
                },
            });
        });
        return () => { if (chartRef.current) chartRef.current.destroy(); };
    }, [breakdown, currency, currLabel, prevLabel]);

    if (!breakdown.length) return null;

    return (
        <div className="chart-card chart-card-wide">
            <div className="chart-card-header">
                <span className="chart-card-title">Revenue by {crewPlural}</span>
                <div className="chart-legend">
                    <span className="chart-legend-item">
                        <span className="chart-legend-dot" style={{ background: COLORS.green }} />
                        {currLabel}
                    </span>
                    <span className="chart-legend-item">
                        <span className="chart-legend-dot" style={{ background: COLORS.green, opacity: 0.4 }} />
                        {prevLabel}
                    </span>
                </div>
            </div>
            <div style={{ position: "relative", height: `${Math.max(220, breakdown.length * 52 + 60)}px` }}>
                <canvas ref={ref} />
            </div>
        </div>
    );
};

// ── Crew Details Table ────────────────────────────────────────────────────────
const CrewDetailsTable = ({ metrics, currency, thisPeriodLabel, prevPeriodLabel, crewSingular = "Crew", crewPlural = "Crew" }) => {
    const breakdown = metrics.crewBreakdown || [];
    if (!breakdown.length) return null;

    const currLabel = thisPeriodLabel || "This Period";
    const prevLabel = prevPeriodLabel || "Prev Period";

    return (
        <div className="chart-card chart-card-wide" style={{ marginTop: "16px" }}>
            <div className="chart-card-header">
                <span className="chart-card-title">{crewPlural} Details</span>
                <span className="chart-card-sub">
                    {currLabel} vs {prevLabel}
                </span>
            </div>
            <div className="crew-table-wrap">
                <table className="crew-table">
                    <thead>
                        <tr>
                            <th>{crewSingular}</th>
                            <th>Jobs ({currLabel})</th>
                            <th>Jobs ({prevLabel})</th>
                            <th>Revenue ({currLabel})</th>
                            <th>Revenue ({prevLabel})</th>
                            <th>Change</th>
                        </tr>
                    </thead>
                    <tbody>
                        {breakdown.map((c) => {
                            // Priority: service_provider_name → crew_name → Crew #id
                            const rawName = c.service_provider_name || c.crew_name || "";
                            const name = String(rawName).trim() !== "" && isNaN(Number(rawName))
                                ? String(rawName).trim()
                                : c.crew_id ? `${crewSingular} #${c.crew_id}` : "Unknown";
                            const revChange = c.prevRevenue
                                ? (((c.revenue - c.prevRevenue) / c.prevRevenue) * 100).toFixed(1)
                                : null;
                            const isPos = revChange !== null && Number(revChange) >= 0;

                            return (
                                <tr key={c.crew_id}>
                                    <td>
                                        <div className="crew-name-cell">
                                            <span className="crew-avatar">
                                                {name.charAt(0).toUpperCase()}
                                            </span>
                                            {name}
                                        </div>
                                    </td>
                                    <td className="crew-td-num">{c.jobs ?? 0}</td>
                                    <td className="crew-td-num crew-td-muted">{c.prevJobs ?? 0}</td>
                                    <td className="crew-td-num">
                                        {currency} {Number(c.revenue || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                                    </td>
                                    <td className="crew-td-num crew-td-muted">
                                        {currency} {Number(c.prevRevenue || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}
                                    </td>
                                    <td>
                                        {revChange !== null ? (
                                            <span className={`crew-change-badge ${isPos ? "pos" : "neg"}`}>
                                                {isPos ? "▲" : "▼"} {Math.abs(revChange)}%
                                            </span>
                                        ) : (
                                            <span className="crew-change-badge neutral">—</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ── Main Export ───────────────────────────────────────────────────────────────
const ChartsSection = ({ metrics, currency, period, thisPeriodLabel, prevPeriodLabel, isClinicService, crewSingular, crewPlural }) => {
    const chartProps = { metrics, currency, thisPeriodLabel, prevPeriodLabel, crewSingular, crewPlural };
    return (
        <div className="charts-section">
            <h2 className="sc-section-title">Charts & Trends</h2>
            <div className="charts-row">
                <BookingsBarChart bookingsByDay={metrics.bookingsByDay} thisPeriodLabel={thisPeriodLabel} prevPeriodLabel={prevPeriodLabel} isClinicService={isClinicService} />
                <JobStatusDonut metrics={metrics} />
            </div>
            <RevenueBarChart   {...chartProps} />
            <CrewDetailsTable  {...chartProps} />
        </div>
    );
};

export default ChartsSection;